/* ===========================================================================
 * n-collection.js — controller for <n-collection> (sections/new-collection-grid)
 *
 * Why this exists instead of reusing assets/facets.js: that file is Dawn's, and
 * it addresses the DOM by Dawn's own class names — #FacetsWrapperDesktop,
 * .js-filter, .mobile-facets__list, #FacetFiltersFormMobile and a surrounding
 * <menu-drawer> whose bindEvents() it calls unconditionally. Rebuilding the
 * markup to be pixel-accurate to Figma means none of those exist, and the
 * Dawn file throws the moment one is missing. This controller has one small
 * contract (documented below) and re-renders the whole section instead of
 * morphing pieces of it, which is both simpler and immune to markup changes.
 *
 * Contract with the Liquid
 *   host          <n-collection class="ncl" data-section-id data-scroll-on-page>
 *   filter inputs any [name] element inside the host is submitted
 *   ajax links    a[data-ncl-link]                (pager, chips, clear all)
 *   accordions    button[data-ncl-accordion] + .ncl-fg__body sibling
 *   load more     button[data-ncl-more] reveals [data-ncl-extra] in its group
 *   sort          [data-ncl-sort] wrapper, button[data-ncl-sort-toggle]
 *   drawer        [data-ncl-drawer-open] / [data-ncl-drawer-close] / .ncl-scrim
 *   price         [data-ncl-range] with [data-ncl-range-thumb=min|max],
 *                 [data-ncl-bubble=min|max], [data-ncl-price-input=min|max]
 *
 * Everything is delegated from the host, so a re-render never needs rebinding.
 * ======================================================================== */
(function () {
  'use strict';

  if (window.customElements && customElements.get('n-collection')) return;

  class NCollection extends HTMLElement {
    connectedCallback() {
      // One AbortController per connection means disconnectedCallback can drop
      // every listener in a single call, and a re-connect (theme editor moving
      // the section) rebinds cleanly instead of double-binding or going deaf.
      this.ac = new AbortController();
      const opts = { signal: this.ac.signal };

      this.sectionId = this.dataset.sectionId;
      this.scrollOnPage = this.dataset.scrollOnPage !== 'false';

      this.addEventListener('click', this.onClick.bind(this), opts);
      this.addEventListener('change', this.onChange.bind(this), opts);
      this.addEventListener('input', this.onInput.bind(this), opts);
      this.addEventListener('submit', this.onSubmit.bind(this), opts);
      this.addEventListener('keydown', this.onHostKeyDown.bind(this), opts);

      document.addEventListener('click', this.onDocumentClick.bind(this), opts);
      document.addEventListener('keydown', this.onDocumentKeyDown.bind(this), opts);
      window.addEventListener('popstate', this.onPopState.bind(this), opts);

      this.syncAllRanges();
    }

    disconnectedCallback() {
      if (this.ac) this.ac.abort();
      this.unlockScroll();
    }

    /* ------------------------------------------------------------ delegation */

    onClick(event) {
      const accordion = event.target.closest('[data-ncl-accordion]');
      if (accordion && this.contains(accordion)) {
        this.toggleAccordion(accordion);
        return;
      }

      const more = event.target.closest('[data-ncl-more]');
      if (more && this.contains(more)) {
        this.revealExtras(more);
        return;
      }

      const sortToggle = event.target.closest('[data-ncl-sort-toggle]');
      if (sortToggle && this.contains(sortToggle)) {
        this.toggleSort();
        return;
      }

      if (event.target.closest('[data-ncl-drawer-open]')) {
        event.preventDefault();
        this.openDrawer();
        return;
      }

      if (event.target.closest('[data-ncl-drawer-close]') || event.target.closest('.ncl-scrim')) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      const link = event.target.closest('a[data-ncl-link]');
      if (link && this.contains(link)) {
        event.preventDefault();
        const isPager = !!link.closest('.ncl-pager');
        this.navigate(link.href, { scroll: isPager && this.scrollOnPage });
      }
    }

    onChange(event) {
      const target = event.target;

      if (target.matches('[data-ncl-range-thumb]')) {
        this.pushRangeToInputs(target.closest('[data-ncl-range]'));
        this.apply();
        return;
      }

      if (target.matches('[data-ncl-price-input]')) {
        this.pullInputsToRange(target.closest('[data-ncl-price]'));
        this.apply();
        return;
      }

      if (target.matches('input[type="radio"][name="sort_by"]')) {
        this.closeSort();
        this.apply();
        return;
      }

      // Only controls that belong to the filter UI trigger a request. Without
      // this guard a variant picker or quantity field inside a product card
      // would re-filter the page.
      if (target.name && this.isFilterControl(target)) this.apply();
    }

    onInput(event) {
      // Dragging updates the fill and the value bubbles only. Firing a request
      // per pointer move would queue dozens of fetches for one gesture; the
      // request goes out on `change`, i.e. when the thumb is released.
      if (event.target.matches('[data-ncl-range-thumb]')) {
        const range = event.target.closest('[data-ncl-range]');
        this.clampRange(range, event.target);
        this.syncRange(range);
        this.mirrorRangeToInputs(range);
      }
    }

    onSubmit(event) {
      // Only the facets form. The add-to-cart forms in the cards also submit
      // through here on their way up, and swallowing those would break ATC.
      if (!event.target.matches('.ncl-facets__form')) return;
      // The <form> exists for the no-JS path; with JS the request is ours.
      event.preventDefault();
      this.apply();
    }

    onHostKeyDown(event) {
      if (event.key !== 'Enter') return;
      if (event.target.matches('[data-ncl-price-input]')) {
        event.preventDefault();
        this.pullInputsToRange(event.target.closest('[data-ncl-price]'));
        this.apply();
      }
    }

    onDocumentClick(event) {
      if (!this.sortOpen) return;
      if (event.target.closest('[data-ncl-sort]')) return;
      this.closeSort();
    }

    onDocumentKeyDown(event) {
      if (event.key !== 'Escape') return;
      if (this.sortOpen) {
        this.closeSort();
        const toggle = this.querySelector('[data-ncl-sort-toggle]');
        if (toggle) toggle.focus();
        return;
      }
      if (this.classList.contains('is-drawer-open')) this.closeDrawer();
    }

    onPopState() {
      // Back/forward: render what the URL now says, without pushing again.
      this.render(window.location.search.replace(/^\?/, ''), { push: false });
    }

    // (Removed: the header-height measurement existed only to offset the desktop
    // sticky sidebar, which is no longer a feature.)

    /* ------------------------------------------------------------- accordion */

    toggleAccordion(button) {
      const open = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', open ? 'false' : 'true');
    }

    revealExtras(button) {
      const group = button.closest('[data-ncl-group]');
      if (!group) return;
      group.querySelectorAll('[data-ncl-extra]').forEach((item) => item.classList.remove('is-hidden'));
      group.dataset.nclExpanded = 'true';
      button.remove();
    }

    /* ------------------------------------------------------------- sort menu */

    get sortOpen() {
      const sort = this.querySelector('[data-ncl-sort]');
      return !!sort && sort.classList.contains('is-open');
    }

    toggleSort() {
      this.sortOpen ? this.closeSort() : this.openSort();
    }

    openSort() {
      const sort = this.querySelector('[data-ncl-sort]');
      const toggle = this.querySelector('[data-ncl-sort-toggle]');
      if (!sort) return;
      sort.classList.add('is-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      const checked = sort.querySelector('input:checked');
      if (checked) checked.focus();
    }

    closeSort() {
      const sort = this.querySelector('[data-ncl-sort]');
      const toggle = this.querySelector('[data-ncl-sort-toggle]');
      if (sort) sort.classList.remove('is-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    /* ---------------------------------------------------------- filter drawer */

    openDrawer() {
      this.lastFocus = document.activeElement;
      this.classList.add('is-drawer-open');
      this.lockScroll();
      const close = this.querySelector('[data-ncl-drawer-close]');
      if (close) close.focus();
    }

    closeDrawer() {
      this.classList.remove('is-drawer-open');
      this.unlockScroll();
      if (this.lastFocus && document.contains(this.lastFocus)) {
        this.lastFocus.focus();
      } else {
        const open = this.querySelector('[data-ncl-drawer-open]');
        if (open) open.focus();
      }
      this.lastFocus = null;
    }

    lockScroll() {
      if (this.scrollLocked) return;
      this.scrollLocked = true;
      this.savedBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      this.scrollLocked = false;
      document.body.style.overflow = this.savedBodyOverflow || '';
    }

    /* -------------------------------------------------------- price / range */

    syncAllRanges() {
      this.querySelectorAll('[data-ncl-range]').forEach((range) => this.syncRange(range));
    }

    rangeParts(range) {
      if (!range) return null;
      const min = range.querySelector('[data-ncl-range-thumb="min"]');
      const max = range.querySelector('[data-ncl-range-thumb="max"]');
      if (!min || !max) return null;
      const ceiling = Number(range.dataset.ceiling) || Number(max.max) || 1;
      return { min, max, ceiling };
    }

    // Stop the two thumbs passing through each other: whichever one the shopper
    // is dragging gives way at the other's value.
    clampRange(range, moved) {
      const p = this.rangeParts(range);
      if (!p) return;
      const a = Number(p.min.value);
      const b = Number(p.max.value);
      if (a <= b) return;
      if (moved === p.min) p.min.value = String(b);
      else p.max.value = String(a);
    }

    syncRange(range) {
      const p = this.rangeParts(range);
      if (!p) return;
      const ceiling = p.ceiling || 1;
      const a = Math.min(Number(p.min.value), Number(p.max.value));
      const b = Math.max(Number(p.min.value), Number(p.max.value));
      const pctA = Math.max(0, Math.min(100, (a / ceiling) * 100));
      const pctB = Math.max(0, Math.min(100, (b / ceiling) * 100));
      range.style.setProperty('--ncl-range-a', pctA + '%');
      range.style.setProperty('--ncl-range-b', pctB + '%');

      const wrap = range.closest('[data-ncl-price]') || range.parentElement;
      if (!wrap) return;
      wrap.style.setProperty('--ncl-range-a', pctA + '%');
      wrap.style.setProperty('--ncl-range-b', pctB + '%');

      const symbol = range.dataset.currency || '';
      const bubbleMin = wrap.querySelector('[data-ncl-bubble="min"]');
      const bubbleMax = wrap.querySelector('[data-ncl-bubble="max"]');
      if (bubbleMin) bubbleMin.textContent = symbol + a;
      if (bubbleMax) bubbleMax.textContent = symbol + b;
    }

    // Visual-only mirror used while dragging.
    mirrorRangeToInputs(range) {
      const p = this.rangeParts(range);
      const wrap = range && range.closest('[data-ncl-price]');
      if (!p || !wrap) return;
      const lo = wrap.querySelector('[data-ncl-price-input="min"]');
      const hi = wrap.querySelector('[data-ncl-price-input="max"]');
      if (lo) lo.value = String(Math.min(Number(p.min.value), Number(p.max.value)));
      if (hi) hi.value = String(Math.max(Number(p.min.value), Number(p.max.value)));
    }

    // Called on release. Blanks a bound that is back at its limit so the URL
    // does not carry a redundant filter.v.price.gte=0.
    pushRangeToInputs(range) {
      const p = this.rangeParts(range);
      const wrap = range && range.closest('[data-ncl-price]');
      if (!p || !wrap) return;
      const lo = wrap.querySelector('[data-ncl-price-input="min"]');
      const hi = wrap.querySelector('[data-ncl-price-input="max"]');
      const a = Math.min(Number(p.min.value), Number(p.max.value));
      const b = Math.max(Number(p.min.value), Number(p.max.value));
      if (lo) lo.value = a > 0 ? String(a) : '';
      if (hi) hi.value = b < p.ceiling ? String(b) : '';
      this.syncRange(range);
    }

    pullInputsToRange(wrap) {
      if (!wrap) return;
      const range = wrap.querySelector('[data-ncl-range]');
      const p = this.rangeParts(range);
      if (!p) return;
      const lo = wrap.querySelector('[data-ncl-price-input="min"]');
      const hi = wrap.querySelector('[data-ncl-price-input="max"]');

      let a = lo && lo.value !== '' ? Number(lo.value) : 0;
      let b = hi && hi.value !== '' ? Number(hi.value) : p.ceiling;
      if (!isFinite(a)) a = 0;
      if (!isFinite(b)) b = p.ceiling;
      a = Math.max(0, Math.min(p.ceiling, a));
      b = Math.max(0, Math.min(p.ceiling, b));
      if (a > b) { const t = a; a = b; b = t; }

      p.min.value = String(a);
      p.max.value = String(b);
      if (lo) lo.value = a > 0 ? String(a) : '';
      if (hi) hi.value = b < p.ceiling ? String(b) : '';
      this.syncRange(range);
    }

    /* ------------------------------------------------------------- rendering */

    // The two subtrees that own filter state. Deliberately narrow: the product
    // cards below also contain named inputs (`name="id"` on every add-to-cart
    // form, quantity fields, variant pickers) and sweeping the whole host would
    // push those into the filter URL.
    get controlScopes() {
      return this.querySelectorAll('.ncl-facets__form, [data-ncl-sort]');
    }

    isFilterControl(el) {
      return !!el.closest('.ncl-facets__form, [data-ncl-sort]');
    }

    collectParams() {
      const params = new URLSearchParams();
      this.controlScopes.forEach((scope) => {
        scope.querySelectorAll('[name]').forEach((el) => {
          if (!el.name || el.disabled) return;
          if (el.type === 'checkbox' || el.type === 'radio') {
            if (el.checked) params.append(el.name, el.value);
          } else if (el.value !== '' && el.value != null) {
            params.append(el.name, el.value);
          }
        });
      });
      return params.toString();
    }

    // A filter change always returns to page 1 — `page` is simply never
    // collected, because no input carries it.
    apply() {
      this.navigateParams(this.collectParams(), { scroll: false });
    }

    navigate(href, options) {
      let search = '';
      try {
        search = new URL(href, window.location.origin).search.replace(/^\?/, '');
      } catch (e) {
        const i = href.indexOf('?');
        search = i === -1 ? '' : href.slice(i + 1);
      }
      this.navigateParams(search, options);
    }

    navigateParams(params, options) {
      this.render(params, Object.assign({ push: true }, options || {}));
    }

    async render(params, options) {
      const opts = options || {};
      const base = window.location.pathname;

      if (opts.push !== false) {
        history.pushState({ nclParams: params }, '', params ? base + '?' + params : base);
      }

      // Supersede an in-flight request: rapid checkbox clicks would otherwise
      // race and the slower response could overwrite the newer one.
      if (this.fetchAc) this.fetchAc.abort();
      this.fetchAc = new AbortController();

      this.classList.add('is-loading');
      const state = this.captureUiState();

      try {
        const url = base + '?' + (params ? params + '&' : '') + 'section_id=' + this.sectionId;
        const response = await fetch(url, { signal: this.fetchAc.signal });
        if (!response.ok) throw new Error('HTTP ' + response.status);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('n-collection');
        if (!fresh) throw new Error('n-collection missing from section response');

        this.innerHTML = fresh.innerHTML;
        this.restoreUiState(state);
        this.syncAllRanges();

        if (opts.scroll) {
          const top = this.getBoundingClientRect().top + window.scrollY - 24;
          window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
        }

        this.dispatchEvent(new CustomEvent('ncl:rendered', { bubbles: true }));
      } catch (error) {
        if (error.name === 'AbortError') return;
        // A failed partial render must not leave the shopper on a stale list
        // that disagrees with the URL — hand over to a full page load.
        console.error('[n-collection]', error);
        window.location.href = base + (params ? '?' + params : '');
      } finally {
        this.classList.remove('is-loading');
      }
    }

    /* The server decides accordion state from which filters are active, which
       would silently re-open a group the shopper had just collapsed. Capture
       their choices by group label and re-apply them after the swap. */
    captureUiState() {
      const collapsed = [];
      const expanded = [];
      this.querySelectorAll('[data-ncl-group]').forEach((group) => {
        const labelEl = group.querySelector('.ncl-fg__label');
        const key = labelEl ? labelEl.textContent.trim() : '';
        if (!key) return;
        const head = group.querySelector('[data-ncl-accordion]');
        if (head && head.getAttribute('aria-expanded') === 'false') collapsed.push(key);
        if (group.dataset.nclExpanded === 'true') expanded.push(key);
      });

      const form = this.querySelector('.ncl-facets__form');
      return { collapsed, expanded, scroll: form ? form.scrollTop : 0 };
    }

    restoreUiState(state) {
      if (!state) return;
      this.querySelectorAll('[data-ncl-group]').forEach((group) => {
        const labelEl = group.querySelector('.ncl-fg__label');
        const key = labelEl ? labelEl.textContent.trim() : '';
        if (!key) return;

        if (state.collapsed.indexOf(key) !== -1) {
          const head = group.querySelector('[data-ncl-accordion]');
          if (head) head.setAttribute('aria-expanded', 'false');
        }
        if (state.expanded.indexOf(key) !== -1) {
          const more = group.querySelector('[data-ncl-more]');
          if (more) this.revealExtras(more);
        }
      });

      const form = this.querySelector('.ncl-facets__form');
      if (form && state.scroll) form.scrollTop = state.scroll;
    }
  }

  customElements.define('n-collection', NCollection);
})();
